import { formatDate } from '@/lib/utils';

export interface RequirementDisplayFields {
  id?: string | null;
  requirement_number?: string | null;
  work_date?: string | null;
  work_end_date?: string | null;
  work_location?: string | null;
  activity_description?: string | null;
  priority?: string | null;
  labour_item_count?: number | null;
  project_id?: string | null;
  project_name?: string | null;
  block_id?: string | null;
  block_name?: string | null;
  activity_id?: string | null;
  activity_name?: string | null;
  status?: string | null;
  total_required_labour?: number | null;
  worker_categories?: string[] | string | null;
  worker_category_items?: Array<{ categoryName: string; count: number }> | null;
}

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low Priority',
  normal: 'Normal Priority',
  high: 'High Priority',
  urgent: 'Urgent Priority',
};

function clean(value: string | null | undefined): string {
  const t = (value ?? '').trim();
  if (!t || t.toLowerCase() === 'undefined' || t.toLowerCase() === 'null') return '';
  return t;
}

/**
 * Requirement dates in the app's en-IN format, e.g. "14 Oct 2026 – 16 Oct 2026"
 * or "16 Oct 2026" if single-day.
 */
export function formatRequirementRange(start?: string | null, end?: string | null): string {
  const s = clean(start);
  const e = clean(end);
  if (!s && !e) return '';
  const ds = s ? new Date(s) : null;
  const de = e ? new Date(e) : null;
  const sd = ds && !isNaN(ds.getTime()) ? ds : null;
  const ed = de && !isNaN(de.getTime()) ? de : null;
  if (!sd && !ed) return '';
  if (sd && ed) {
    if (s === e) return formatDate(s);
    return `${formatDate(s)} – ${formatDate(e)}`;
  }
  if (sd) return formatDate(s);
  return formatDate(e);
}

export function priorityLabel(priority?: string | null): string {
  const p = clean(priority).toLowerCase();
  if (!p) return '';
  return PRIORITY_LABELS[p] ?? clean(priority);
}

/**
 * Primary title: Project name, bold and prominent.
 * Falls back to Requirement ID or 'Requirement' if project name is unavailable.
 */
export function requirementPrimaryLabel(r: RequirementDisplayFields): string {
  return clean(r.project_name) || clean(r.requirement_number) || 'Requirement';
}

/**
 * Additional details formatted line by line in clear hierarchy:
 * 1. Project name (primary label)
 * 2. Block: ...
 * 3. Worker categories: ...
 * 4. Required labour: ...
 * 5. Requirement date: ...
 */
export function requirementSecondaryParts(r: RequirementDisplayFields): string[] {
  const parts: string[] = [];

  const block = clean(r.block_name);
  if (block) {
    parts.push(`Block: ${block}`);
  }

  let categoriesStr = '';
  if (Array.isArray(r.worker_categories) && r.worker_categories.length > 0) {
    categoriesStr = r.worker_categories.map((c) => clean(c)).filter(Boolean).join(', ');
  } else if (typeof r.worker_categories === 'string' && clean(r.worker_categories)) {
    categoriesStr = clean(r.worker_categories);
  } else if (Array.isArray(r.worker_category_items) && r.worker_category_items.length > 0) {
    categoriesStr = [...new Set(r.worker_category_items.map((i) => clean(i.categoryName)).filter(Boolean))].join(', ');
  }
  if (categoriesStr) {
    parts.push(`Worker categories: ${categoriesStr}`);
  }

  let totalLabour = r.total_required_labour != null ? Number(r.total_required_labour) : null;
  if ((totalLabour == null || isNaN(totalLabour)) && Array.isArray(r.worker_category_items) && r.worker_category_items.length > 0) {
    totalLabour = r.worker_category_items.reduce((sum, i) => sum + (Number(i.count) || 0), 0);
  }
  if (totalLabour != null && !isNaN(totalLabour)) {
    parts.push(`Required labour: ${totalLabour}`);
  }

  const dateRange = formatRequirementRange(r.work_date, r.work_end_date);
  if (dateRange) {
    parts.push(`Requirement date: ${dateRange}`);
  }

  return parts;
}

export function requirementSecondaryLabel(r: RequirementDisplayFields): string {
  return requirementSecondaryParts(r).join('\n');
}

/**
 * Everything the search box should match against:
 * project name, requirement description, block, requirement ID, dates, activity, and worker categories.
 */
export function requirementSearchText(r: RequirementDisplayFields): string {
  const catStr = Array.isArray(r.worker_categories)
    ? r.worker_categories.join(' ')
    : clean(r.worker_categories);
  const catItemsStr = Array.isArray(r.worker_category_items)
    ? r.worker_category_items.map((i) => `${i.categoryName} ${i.count}`).join(' ')
    : '';
  return [
    requirementPrimaryLabel(r),
    requirementSecondaryLabel(r),
    clean(r.project_name),
    clean(r.work_location),
    clean(r.activity_description),
    clean(r.block_name),
    clean(r.requirement_number),
    clean(r.activity_name),
    clean(r.work_date),
    clean(r.work_end_date),
    formatRequirementRange(r.work_date, r.work_end_date),
    catStr,
    catItemsStr,
    r.total_required_labour != null ? String(r.total_required_labour) : '',
    priorityLabel(r.priority),
  ]
    .filter(Boolean)
    .join(' ');
}
